import { openModal, type ModalHandle } from '@smartsash/ui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { CertificationCreateForm } from './CertificationCreateForm';

/** The `<form>` the dialog's footer submits. One dialog is open at a time, so one id is enough. */
const FORM_ID = 'certification-create-form';

/**
 * Open the create-programme dialog (HS-1205).
 *
 * Imperative rather than a rendered `<Modal>`, because only `ModalProvider` draws the backdrop and
 * centres the card — a standalone `Modal` renders exactly where it is written, and this dialog is
 * opened from the list's header slot, where it would lay out as another header control beside the
 * search box.
 *
 * The footer's Create button cannot be a `type="submit"` button — `ModalAction` renders its own —
 * so it calls `requestSubmit()` on the form by id. That keeps validation, the resolver and the
 * error rendering inside the form, and leaves the dialog owning only its own chrome.
 * `closeOnClick: false` is what stops a failed submit from dismissing the dialog and taking the
 * validation messages with it.
 */
export function useCreateCertificationDialog(): () => void {
  const { t } = useTranslation();

  return useCallback(() => {
    // `handle` is referenced by the body's `onCreated` closure, which cannot run before
    // `openModal` has returned: the modal store renders the body on a later tick, and the callback
    // fires only after the server answers.
    const handle: ModalHandle = openModal({
      title: t('certifications.form.addTitle'),
      description: t('certifications.form.addDescription'),
      icon: false,
      body: (
        <CertificationCreateForm
          formId={FORM_ID}
          onCreated={() => {
            handle.close({ action: 'created' });
          }}
        />
      ),
      actions: [
        { label: t('common.cancel'), value: 'cancel' },
        {
          label: t('common.create'),
          value: 'create',
          hierarchy: 'primary',
          closeOnClick: false,
          onClick: () => {
            const form = document.getElementById(FORM_ID);
            if (form instanceof HTMLFormElement) form.requestSubmit();
          },
        },
      ],
    });
  }, [t]);
}
